function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from 'react';
import { parseDate, parseDateWithoutZ, PRINT_DATE_FORMAT, PRINT_DATE_TIME_FORMAT, printInputDate, printInputDateTime, printTime, roundToQuarter } from '@guryou/utils';
import { DateTimePicker as MuiDateTimePicker, KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import PropTypes from 'prop-types';
import { Block } from '../content';
import HiddenField from './HiddenField';
const DateTimePicker = ({
  dateFieldName,
  timeFieldName,
  value,
  onChange,
  startWithNullDate = false,
  disabled,
  disableField = false,
  margin = 'normal',
  xs,
  md,
  lg,
  errorMessage,
  shouldDisableDate,
  variant = 'inline',
  noNameField,
  z = true,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(!value && startWithNullDate ? roundToQuarter(value, startWithNullDate) : z ? parseDate(value, startWithNullDate) : parseDateWithoutZ(value, startWithNullDate));
  const triggerChangeForHiddenField = value => {
    if (!dateFieldName) {
      return;
    }
    const element = document.getElementById(dateFieldName);
    const lastValue = element.value;
    element.value = value;
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(lastValue);
    }
    const event = new Event('input', {
      target: element,
      bubbles: true
    });
    element.dispatchEvent(event);
  };
  const onChangeHandler = date => {
    if (!date || !date._isAMomentObject || date.isValid()) {
      // `date` here is what MUI's picker (DateFnsUtils adapter — see App.js's
      // MuiPickersUtilsProvider) hands back on user interaction: a native `Date`
      // representing a real, already-correct absolute instant for whatever the user
      // picked in the browser's own local timezone. `printInputDateTime`'s `utc`
      // parameter defaults to true, which runs the value through
      // `utcToZonedTime(date, 'UTC')` — a redisplay step meant for a stored,
      // "floating"/Z-labeled wall-clock STRING (this codebase's convention for times
      // that must show identical digits regardless of viewer timezone), not for a
      // real absolute Date the picker widget just computed from a live local
      // selection. Applying it here re-introduced the local UTC offset a second
      // time, so picking e.g. 15:00 in a UTC+2 browser silently stored/redisplayed
      // 13:00 (confirmed by reproducing the exact shift via date-fns-tz directly).
      // `utc: false` skips that step, so the stored value is a plain local
      // `format()` of the Date the widget already got right — no shift. This was
      // also mis-called as `printInputDateTime(date, z)`, which only coincidentally
      // produced `utc: true` in the common default case since both `z` and `utc`
      // default `true` — that positional-argument slip is fixed here too, though it
      // was never the actual cause (parseDate/parseDateWithoutZ both short-circuit
      // unchanged for native Date input regardless of `z`, so that argument only
      // ever mattered for the un-taken string-parsing branch).
      const val = printInputDateTime(date, false, true, z);
      setLocalValue(val);
      onChange && onChange(val);
      triggerChangeForHiddenField(val);
    }
  };
  let content = null;
  if (!xs && !md && !lg) {
    rest.margin = margin;
  }
  if (dateFieldName && timeFieldName) {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
      id: dateFieldName,
      style: {
        display: 'none'
      },
      disabled: disabled,
      name: dateFieldName,
      value: printInputDate(localValue, z)
    }), /*#__PURE__*/React.createElement(HiddenField, {
      disabled: disabled,
      name: timeFieldName,
      value: printTime(localValue, z, z)
    }), /*#__PURE__*/React.createElement(MuiDateTimePicker, _extends({
      inputVariant: "outlined",
      InputProps: {
        ...rest.InputProps,
        name: null
      },
      format: PRINT_DATE_TIME_FORMAT
    }, rest, {
      autoOk: true,
      variant: variant,
      value: localValue,
      onChange: onChangeHandler,
      helperText: errorMessage,
      error: !!errorMessage,
      shouldDisableDate: shouldDisableDate,
      orientation: "portrait"
    })));
  } else if (dateFieldName) {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
      id: dateFieldName,
      style: {
        display: 'none'
      },
      disabled: disabled,
      name: dateFieldName,
      value: printInputDate(localValue, z)
    }), /*#__PURE__*/React.createElement(KeyboardDatePicker, _extends({
      inputVariant: "outlined"
    }, rest, {
      InputProps: {
        ...rest.InputProps,
        name: null
      },
      KeyboardButtonProps: {
        style: {
          marginRight: '8px'
        }
      },
      format: PRINT_DATE_FORMAT,
      autoOk: true,
      variant: variant,
      value: localValue,
      onChange: onChangeHandler,
      helperText: errorMessage,
      error: !!errorMessage,
      disabled: disabled,
      shouldDisableDate: shouldDisableDate,
      orientation: "portrait"
    })));
  } else if (noNameField) {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
      id: 'noNameField',
      style: {
        display: 'none'
      },
      disabled: disabled,
      value: printInputDate(localValue, z)
    }), /*#__PURE__*/React.createElement(KeyboardDatePicker, _extends({
      inputVariant: "outlined"
    }, rest, {
      InputProps: {
        ...rest.InputProps,
        name: null
      },
      KeyboardButtonProps: {
        style: {
          marginRight: '8px'
        }
      },
      format: PRINT_DATE_FORMAT,
      autoOk: true,
      variant: variant,
      value: localValue,
      onChange: onChangeHandler,
      helperText: errorMessage,
      error: !!errorMessage,
      disabled: disabled,
      shouldDisableDate: shouldDisableDate,
      orientation: "portrait"
    })));
  } else {
    content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HiddenField, {
      disabled: disabled,
      name: timeFieldName,
      value: printTime(localValue, z, z) || ''
    }), /*#__PURE__*/React.createElement(KeyboardTimePicker, _extends({
      inputVariant: "outlined"
    }, rest, {
      InputProps: {
        ...rest.InputProps,
        name: null
      },
      KeyboardButtonProps: {
        style: {
          marginRight: '8px'
        }
      },
      autoOk: true,
      variant: variant,
      value: localValue,
      onChange: onChangeHandler,
      helperText: errorMessage,
      error: !!errorMessage,
      ampm: false,
      minutesStep: 5,
      disabled: disableField,
      shouldDisableDate: shouldDisableDate,
      orientation: "portrait"
    })));
  }
  if (xs || md || lg) {
    let marginProps;
    if (margin === 'normal') {
      marginProps = {
        margin: 4,
        marginBottom: 2
      };
    } else if (margin === 'none') {
      marginProps = {
        margin: 0
      };
    } else if (margin === 'wizard') {
      marginProps = {
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 2,
        marginRight: 2
      };
    } else {
      marginProps = {
        margin
      };
    }
    return /*#__PURE__*/React.createElement(Block, _extends({
      xs: xs,
      md: md,
      lg: lg
    }, marginProps, {
      style: {
        overflowY: 'visible'
      }
    }), content);
  }
  return content;
};
DateTimePicker.propTypes = {
  disablePast: PropTypes.bool,
  format: PropTypes.string,
  dateFieldName: PropTypes.string,
  timeFieldName: PropTypes.string
};
DateTimePicker.defaultProps = {
  disablePast: true,
  disabled: false
};
export default DateTimePicker;